import { orderBy } from "lodash-es";

import Announcement from "@mapmycustomers/shared/types/announcements/Announcement";
import { createFilterMatcher, InvalidFilterError } from "@mapmycustomers/shared/util/filterMatcher";
import { parseUrl } from "@mapmycustomers/shared/util/url";

import AnnouncementFilterContext from "@app/types/AnnouncementFilterContext";
import { parseApiDate } from "@app/util/parsers";

import announcementFilterSchema from "./announcementFilterSchema";
import { getNavigablePathForPageId, isKnownPageId } from "./pageRegistry";

const SAFE_EXTERNAL_URL_PROTOCOLS = ["http:", "https:"];
// A link resolves "https:host" against the current page, so the slashes are required.
const ABSOLUTE_HTTP_URL_PREFIX = /^https?:\/\//i;
// Each open shows at most this many; the others wait for a later open.
const MAX_ANNOUNCEMENTS = 6;

const isSafeExternalUrl = (value: string): boolean => {
  const url = parseUrl(value);
  return (
    ABSOLUTE_HTTP_URL_PREFIX.test(value) &&
    !!url?.host &&
    SAFE_EXTERNAL_URL_PROTOCOLS.includes(url.protocol)
  );
};

interface EligibilityResult {
  matches: boolean;
  skipReason?: string;
  unexpectedError?: unknown; // a filter failure other than an invalid filter
}

const evaluateEligibility = (
  announcement: Announcement,
  filterContext: AnnouncementFilterContext
): EligibilityResult => {
  if (announcement.ctaPageId && announcement.ctaExternalUrl) {
    return { matches: false, skipReason: "both a cta page id and a cta url are set" };
  }
  if (announcement.ctaExternalUrl && !isSafeExternalUrl(announcement.ctaExternalUrl)) {
    return { matches: false, skipReason: `unsafe cta url "${announcement.ctaExternalUrl}"` };
  }
  if (announcement.ctaPageId && !isKnownPageId(announcement.ctaPageId)) {
    return { matches: false, skipReason: `unknown cta page id "${announcement.ctaPageId}"` };
  }
  if (announcement.ctaPageId && !getNavigablePathForPageId(announcement.ctaPageId)) {
    return {
      matches: false,
      skipReason: `display-only cta page id "${announcement.ctaPageId}"`,
    };
  }
  try {
    // A missing filters field targets everyone, like a null one.
    const matchesFilter = createFilterMatcher(announcement.filters, announcementFilterSchema);
    return { matches: matchesFilter(filterContext) };
  } catch (error) {
    // Any failure skips and reports this one announcement; it must never break the app.
    return {
      matches: false,
      skipReason: error instanceof Error ? error.message : String(error),
      unexpectedError: error instanceof InvalidFilterError ? undefined : error,
    };
  }
};

const selectEligibleAnnouncements = (
  announcements: Announcement[],
  filterContext: AnnouncementFilterContext,
  onSkip: (announcement: Announcement, reason: string, unexpectedError?: unknown) => void
): Announcement[] => {
  const eligible = announcements.filter((announcement) => {
    const { matches, skipReason, unexpectedError } = evaluateEligibility(
      announcement,
      filterContext
    );
    if (skipReason !== undefined) {
      onSkip(announcement, skipReason, unexpectedError);
      return false;
    }
    return matches;
  });

  return orderBy(
    eligible,
    [(announcement) => parseApiDate(announcement.updatedAt), (announcement) => announcement.id],
    ["desc", "desc"]
  ).slice(0, MAX_ANNOUNCEMENTS);
};

export default selectEligibleAnnouncements;
